import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Only the default / fallback language is bundled into the main chunk. The
// other six locale files (~450 KB of JSON combined) are code-split and fetched
// on demand — this is the single biggest cut to the initial JS payload.
import kg from './kg.json';
import { KG_LOCATIONS } from '../app/data/kgLocations.js';

const LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> =
  {
    ru: () => import('./ru.json'),
    kz: () => import('./kz.json'),
    uz: () => import('./uz.json'),
    tr: () => import('./tr.json'),
    en: () => import('./en.json'),
    zh: () => import('./zh.json'),
  };

export const SUPPORTED_LANGS = ['kg', 'ru', 'kz', 'uz', 'tr', 'en', 'zh'];

function initialLang(): string {
  try {
    const v =
      localStorage.getItem('app-language') || localStorage.getItem('lang');
    if (v && SUPPORTED_LANGS.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return 'kg';
}

i18n.use(initReactI18next).init({
  resources: { kg: { translation: kg } },
  lng: 'kg',
  fallbackLng: 'kg',
  supportedLngs: SUPPORTED_LANGS,
  interpolation: { escapeValue: false },
  // We manage async locale loading ourselves (see loadLanguage), so Suspense
  // isn't needed and would otherwise throw for the not-yet-loaded bundles.
  react: { useSuspense: false },
});

// Plurals: «kg» and «kz» aren't real locale codes (Kyrgyz is ky, Kazakh kk).
// Intl.PluralRules("kg") falls back to the device locale — on an iPhone set to
// Russian, Safari answers "few" for 2, there's no `_few` key in kg.json and the
// raw key («assistant.card.rooms») showed up. Resolve plurals by the real codes.
const PLURAL_LOCALE: Record<string, string> = { kg: 'ky', kz: 'kk' };
const pluralResolver = i18n.services.pluralResolver as unknown as {
  getRule: (code: string, options?: object) => Intl.PluralRules | undefined;
};
const getRule = pluralResolver.getRule.bind(pluralResolver);
pluralResolver.getRule = (code, options) => getRule(PLURAL_LOCALE[code] ?? code, options);

// Every settlement name (scripts/build-kg-locations.mjs) as a `districts.*`
// fallback in the fallback language: all t(`districts.${id}`) calls show a
// real name for the ~1 100 new ids; existing translations are never replaced.
i18n.addResourceBundle(
  'kg',
  'translation',
  { districts: Object.fromEntries(Object.values(KG_LOCATIONS).flat().map((d) => [d.id, d.name])) },
  true,
  false,
);


// <html lang> follows the interface language (BCP 47: kg → ky, kz → kk) —
// it was fixed at "ru" even on the Kyrgyz site. Hub pages under /ky/, /kz/…
// switch i18n to their language, so their prerendered HTML gets it too.
const HTML_LANG: Record<string, string> = { kg: 'ky', kz: 'kk', ru: 'ru', uz: 'uz', tr: 'tr', en: 'en', zh: 'zh' };
const syncHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = HTML_LANG[lng] ?? 'ky';
};
i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang(i18n.language);

/** Fetch a locale bundle (if not the default and not already loaded) and
 *  switch to it. Safe to call repeatedly. */
export async function loadLanguage(lng: string): Promise<void> {
  const target = SUPPORTED_LANGS.includes(lng) ? lng : 'kg';
  if (target !== 'kg' && !i18n.hasResourceBundle(target, 'translation')) {
    try {
      const mod = await LOADERS[target]();
      i18n.addResourceBundle(target, 'translation', mod.default, true, true);
    } catch {
      // Network hiccup — fall back to the bundled default rather than crash.
    }
  }
  if (i18n.language !== target) await i18n.changeLanguage(target);
}

/** Resolves once the saved language is applied — awaited in main.tsx so the
 *  first paint is already in the right language (no flash of the default). */
export const languageReady = loadLanguage(initialLang());

// Dev: an edited locale file is swapped in place. Without this the update
// climbs to routes.tsx, fails on the import cycle there and Vite reloads the
// whole page on every save of a .json file.
if (import.meta.hot) {
  const LANGS = ['kg', 'ru', 'kz', 'uz', 'tr', 'en', 'zh'];
  import.meta.hot.accept(
    ['./kg.json', './ru.json', './kz.json', './uz.json', './tr.json', './en.json', './zh.json'],
    (mods) => {
      mods.forEach((mod, i) => {
        const lng = LANGS[i];
        if (!mod || (lng !== 'kg' && !i18n.hasResourceBundle(lng, 'translation'))) return;
        i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
      });
      void i18n.changeLanguage(i18n.language);
    },
  );
}

export default i18n;
